import React from "react";
import { Button, Select } from "antd";
import { Plus, X } from "lucide-react";
import { DataTable } from "../../components/DataTable";
import { IProduct } from "../../../types/api/product";
import { getProductColumns } from "./ProductColumns";
import { ProductBulkActions } from "./ProductBulkActions";

const SORT_OPTIONS = [
  { label: "Most Recently Uploaded", value: "" },
  { label: "Name: A-Z", value: "name_asc" },
  { label: "Name: Z-A", value: "name_desc" },
  { label: "By Artist", value: "artist_asc" },
  { label: "By Price", value: "price_asc" },
];

interface ProductPanelProps {
  title: React.ReactNode;
  addLabel: string;
  list: IProduct[];
  total: number;
  loading: boolean;
  isError: boolean;
  queryData: any;
  // When set, the list is scoped to one artist's sculptures (e.g. after
  // opening a product via its "Edit in Admin" deep link from the site) -
  // shown as a dismissible banner so it's clear why the list is filtered.
  artistFilterName?: string;
  onClearArtistFilter?: () => void;
  // search: string;
  onSearchChange: (v: string) => void;
  // statusFilter: string;
  onStatusChange: (v: string) => void;
  onSortChange?: (v: string) => void;
  onPageChange: (page: number, limit: number) => void;
  onAdd: () => void;
  onEdit: (record: IProduct) => void;
  onEditVariants?: (record: IProduct) => void;
  onDelete: (id: string) => void;
  onApprove?: (record: IProduct) => void;
}

const PRODUCT_STATUS_OPTIONS = [
  { label: "All Status", value: "" },
  { label: "Awaiting Approval", value: "pending" },
  { label: "Active", value: "true" },
  { label: "Inactive", value: "false" },
];

export const ProductPanel: React.FC<ProductPanelProps> = ({
  title,
  addLabel,
  list,
  total,
  loading,
  isError,
  queryData,
  artistFilterName,
  onClearArtistFilter,
  onSearchChange,
  onStatusChange,
  onSortChange,
  onPageChange,
  onAdd,
  onEdit,
  onEditVariants,
  onDelete,
  onApprove,
}) => {
  const columns = getProductColumns({
    onEdit,
    onEditVariants,
    onDelete,
    onApprove,
  });

  return (
    <DataTable
      title={
        artistFilterName ? (
          <div className="flex flex-col gap-1.5">
            {title}
            <div className="inline-flex items-center gap-2 w-fit text-xs font-medium text-primary bg-primary/10 border border-primary/20 rounded-full pl-3 pr-1.5 py-1">
              Showing sculptures by {artistFilterName}
              <button
                type="button"
                onClick={onClearArtistFilter}
                aria-label="Clear artist filter"
                className="hover:bg-primary/20 rounded-full p-0.5"
              >
                <X size={12} />
              </button>
            </div>
          </div>
        ) : (
          title
        )
      }
      headerActions={
        <div className="flex items-center gap-2">
          <ProductBulkActions />
          <Button
            type="primary"
            size="small"
            icon={<Plus size={16} />}
            onClick={onAdd}
            className="!bg-primary flex items-center gap-2"
          >
            {addLabel}
          </Button>
        </div>
      }
      columns={columns}
      dataSource={list}
      rowKey="_id"
      loading={loading}
      isError={isError}
      searchProps={{
        searchValue: queryData?.search,
        onSearchChange,
        placeholder: "Search products...",
        statusValue: queryData?.status,
        onStatusChange,
        statusOptions: PRODUCT_STATUS_OPTIONS,
        children: onSortChange ? (
          <Select
            value={queryData?.sortBy || ""}
            onChange={onSortChange}
            options={SORT_OPTIONS}
            className="w-52 h-[35px]"
          />
        ) : undefined,
      }}
      paginationProps={
        queryData
          ? {
              page: queryData.page ?? 1,
              limit: queryData.limit ?? 10,
              total,
              onChange: (page: number, pageSize: number) => onPageChange(page, pageSize),
            }
          : undefined
      }
    />
  );
};
